import React, { Component } from 'react';
import { object, func, bool, array, string, number } from 'prop-types';
import { resolve } from 'library/helpers/path-resolver';
import Query from 'apps/admin/util/query';
import { jsonToInputParams } from 'library/util/graph';
import styled from 'styled-components';
import withColor from 'util/hoc/with-color';
import AnswerChoiceSelectModal from 'apps/admin/pages/questions/answer-choice-select-modal';
import ViewAnswerChoicesModal from 'apps/admin/pages/questions/view_answer_choices';
import QuestionSetDropdown from 'apps/admin/pages/questions/question-set-dropdown';
import QuestionGroupDropdown from 'apps/admin/pages/questions/question-group-dropdown';
import CriteriaDropdown from 'apps/admin/pages/questions/criteria/criteria-dropdown';
import * as ProfileFields from 'apps/journey/forms/profile-field-types';
import * as UPSFields from 'apps/journey/components/user-profile-setup/components/user-profile-setup-modal/components/user-profile-setup-question-manager/question-components';
import PencilSvg from 'library/icons/pencil.svg';
import EyeSvg from 'library/icons/eye.svg';
import { NotificationRender } from 'library/components/notification';
import Checkbox from 'library/components/form-elements/checkbox';
import withData from 'util/hoc/with-data';
import InfoTooltip from 'apps/journey/components/info-tooltip';
import MarkdownTextarea from 'library/components/form-elements/markdown-textarea';
import { cleanObjectKeys } from 'library/util/clean-object-keys';

const renderProfileFieldTypes = [
  ...Object.keys(ProfileFields),
  'MultiSelection',
];
const renderProfileSetupTypes = [...Object.keys(UPSFields)];

const StyledForm = styled.form`
  &&& {
    height: 100%;
    margin: 0;
    display: flex;
    flex-direction: column;
  }
`;

const StyledRow = styled.div`
  display: flex;
  font-weight: 300;
  width: 100%;
`;

const StyledColumn = styled.div`
  flex: 50%;
  margin-left: 1em;
  label {
    font-weight: 600;
    display: inline-flex;
  }
  textarea {
    resize: vertical;
    min-height: 100px;
  }
  .answer-choice-set {
    font-weight: 500;
  }
`;

const SubmitButtons = styled.div`
  display: flex;
  input {
    text-align: center;
    width: 100%;
    margin-bottom: 1em;
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  text-align: center;
`;

const StyledPencilSvg = withColor(styled(PencilSvg)`
  cursor: pointer;
  width: 0.8em;
  margin-bottom: -0.4em;
  margin-left: 0.4em;
  &:hover {
    color: ${(props) => props.colors.secondary};
  }
`);

const StyledEyeSvg = withColor(styled(EyeSvg)`
  cursor: pointer;
  width: 1.5em;
  margin-bottom: -0.4em;
  margin-left: 0.4em;

  &:hover {
    color: ${(props) => props.colors.secondary};
  }
`);

class QuestionForm extends Component {
  state = {
    loading: false,
    question: this.props.question,
    question_group_id: this.props.question_group.id,
    question_set_id: this.props.question_set.id,
    answer_choices: this.props.answer_choices,
    answer_choice_set_title: '',
    choose_answer_choices: false,
  };

  static getDerivedStateFromProps = (props, state) => {
    if (Object.keys(state.question).length === 0 && props.question != null) {
      return {
        question: props.question,
        question_group_id: props.question_group.id,
        question_set_id: props.question_set.id,
        answer_choices: props.answer_choices,
        answer_choice_set_title: resolve(
          props,
          'answer_choices/0/internal_title',
          ''
        ),
        criteria_id: resolve(props, 'question/criteria_id', null),
      };
    }
    return null;
  };

  submitButtonText = () => {
    this.props.question != null ? 'Update' : 'Create';
  };

  handleChange = (event) => {
    const { question } = { ...this.state };
    const currentQuestion = question;
    const { name, value } = event.target;
    currentQuestion[name] = value;
    this.setState({ question: currentQuestion });
  };

  handlePCChange = (event) => {
    const { question } = { ...this.state };
    const currentQuestion = question;
    const { name, value } = event.target;
    currentQuestion.professional_center[name] = value;
    this.setState({ question: currentQuestion });
  };

  handleInternalTitleChange = (event) => {
    const { question } = { ...this.state };
    const currentQuestion = question;
    const { name, value } = event.target;
    currentQuestion[name] = value;
    if (this.props.method == 'new') {
      const title_string = currentQuestion.internal_title;
      const convertedString = title_string
        .split(' ')
        .map((s) => s.toLowerCase())
        .join('_');
      currentQuestion.attribute_to_update = convertedString;
    }

    this.setState({ question: currentQuestion });
  };

  handleQuestionSetorGroupIdChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: parseInt(value) });
  };

  buildQueryParams = () => {
    const queryParams = {
      id: resolve(this, 'state/question/id'),
      internal_title: resolve(this, 'state/question/internal_title'),
      text: resolve(this, 'state/question/text'),
      attribute_to_update: resolve(this, 'state/question/attribute_to_update'),
      tooltip: resolve(this, 'state/question/tooltip'),
      render_type: resolve(this, 'state/question/render_type'),
      filter_name: resolve(this, 'state/question/filter_name'),
      question_group_id: resolve(this, 'state/question_group_id'),
      criteria_id: resolve(this, 'state/criteria_id'),
      expiration_time: resolve(this, 'state/question/expiration_time')
        ? parseInt(this.state.question.expiration_time)
        : null,
      expiration_units: resolve(this, 'state/question/expiration_units'),
      repeatable: resolve(this, 'state/question/repeatable'),
      professional_center: resolve(this, 'state/question/professional_center'),
      required: resolve(this, 'state/question/required', false),
      answer_choice_set_title: resolve(this, 'state/answer_choice_set_title'),
    };

    return cleanObjectKeys(queryParams);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.method === 'edit'
      ? this.updateQuestion()
      : this.createQuestion();
  };

  toggleAttribute = (attribute) => {
    this.setState({
      question: {
        ...this.state.question,
        [attribute]: !this.state.question[attribute],
      },
    });
  };

  togglePCAttribute = (attribute) => {
    this.setState((prevState) => {
      const newQuestion = { ...prevState.question };
      newQuestion.professional_center[attribute] = !newQuestion
        .professional_center[attribute];
      return { ...prevState, newQuestion };
    });
  };

  toggleRequired = () => {
    this.toggleAttribute('required');
  };

  togglePCFilter = () => {
    this.togglePCAttribute('filter');
  };

  togglePCColumn = () => {
    this.togglePCAttribute('column');
  };

  toggleRepeatable = () => {
    this.toggleAttribute('repeatable');
  };

  createQuestion = async () => {
    this.setState({ loading: true });
    try {
      const queryParams = this.buildQueryParams();

      const result = await Query(`
        mutation {
          create_question(${jsonToInputParams(queryParams)}) {
            success
            errors
            question {
              id
            }
          }
        }
      `);

      if (resolve(result, 'data/create_question/errors', false)) {
        NotificationRender('success', 'The question was successfully created!');
        this.props.reload_graph(false, true);
        this.props.handleCancel();
      } else {
        NotificationRender('error', 'Something went wrong. Try again.');
        console.error(result, 'data/create_question/errors/messages', []);
      }
    } catch (e) {
      console.error(e);
    }
    this.setState({ loading: false });
  };

  updateQuestion = async () => {
    this.setState({ loading: true });
    try {
      const queryParams = this.buildQueryParams();

      const result = await Query(`
        mutation {
          update_question(${jsonToInputParams(queryParams)}) {
            success
            errors
            question {
              id
            }
          }
        }
      `);

      if (resolve(result, 'data/update_question/errors', false)) {
        NotificationRender('success', 'The question was successfully updated!');
        this.props.reload_graph(false, true);
        this.props.handleCancel();
      } else {
        NotificationRender('error', 'Something went wrong. Try again.');
        console.error(result, 'data/create_question/errors/messages', []);
      }
    } catch (e) {
      console.error(e);
    }
    this.setState({ loading: false });
  };

  renderAnswerChoiceSetModal = (method) => {
    this.setState({ choose_answer_choices: true });
  };

  renderModalComponentTypes = () => {
    if (this.state.question_set_id) {
      let questionSet = resolve(
        this.props.graph_data,
        'question_sets/results',
        []
      ).find((set) => set.id == this.state.question_set_id);
      if (questionSet) {
        return questionSet.name === 'user_profile_setup';
      }
    }
    return false;
  };

  closeAnswerChoiceSetModal = () =>
    this.setState({ choose_answer_choices: false });

  renderViewAnswerChoiceSetModal = () => {
    this.setState({ view_answer_choices: true });
  };

  closeViewAnswerChoiceSetModal = () =>
    this.setState({ view_answer_choices: false });

  updateAnswerChoiceSet = (answer_choice_set_title) => {
    this.setState({
      choose_answer_choices: false,
      answer_choice_set_title: answer_choice_set_title,
    });
  };

  render = () => (
    <StyledForm onSubmit={this.handleSubmit}>
      <StyledRow>
        <StyledColumn>
          <label>Internal Title</label>
          <input
            value={this.state.question.internal_title}
            name="internal_title"
            onChange={this.handleInternalTitleChange}
          />
        </StyledColumn>
        <StyledColumn>
          <label>Question Set</label>
          <QuestionSetDropdown
            onChange={this.handleQuestionSetorGroupIdChange}
            questionSets={resolve(
              this.props.graph_data,
              'question_sets/results',
              []
            )}
            questionSetId={this.state.question_set_id}
          />
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <label>
            Display Text (External Facing){' '}
            {
              <InfoTooltip
                key="markdwn-info"
                tooltip="The markdown only works on the Markdown render type"
                uid="markdwn-info"
                iconOnly
              />
            }
          </label>
          <MarkdownTextarea
            value={this.state.question.text}
            name="text"
            onChange={this.handleChange}
          />
        </StyledColumn>
        <StyledColumn>
          <label>Question Group</label>
          {this.state.question_set_id && (
            <QuestionGroupDropdown
              graph_data={this.props.graph_data}
              onChange={this.handleQuestionSetorGroupIdChange}
              questionSetId={this.state.question_set_id}
              questionGroupId={this.state.question_group_id}
            />
          )}
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <label>Tooltip (External Facing)</label>
          <textarea
            value={this.state.question.tooltip}
            name="tooltip"
            onChange={this.handleChange}
          />
        </StyledColumn>
        <StyledColumn>
          <label>Answer Choice Set:</label>
          <div className="answer-choice-set">
            {this.state.answer_choice_set_title}
            {this.state.answer_choice_set_title && (
              <StyledEyeSvg onClick={this.renderViewAnswerChoiceSetModal} />
            )}
            <StyledPencilSvg onClick={this.renderAnswerChoiceSetModal} />
            {this.state.view_answer_choices && (
              <ViewAnswerChoicesModal
                graph_data={this.props.graph_data}
                active={true}
                onClose={this.closeViewAnswerChoiceSetModal}
                internalTitle={this.state.answer_choice_set_title}
                method={'view'}
                updateAnswerChoiceSet={this.updateAnswerChoiceSet}
              />
            )}
            {this.state.choose_answer_choices && (
              <AnswerChoiceSelectModal
                graph_data={this.props.graph_data}
                active={true}
                onClose={this.closeAnswerChoiceSetModal}
                method={'edit'}
                updateAnswerChoiceSet={this.updateAnswerChoiceSet}
              />
            )}
          </div>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <label>Field to Update</label>
          <input
            value={this.state.question.attribute_to_update}
            name="attribute_to_update"
            onChange={this.handleChange}
          />
        </StyledColumn>
        <StyledColumn>
          <label>Question Criteria</label>
          <CriteriaDropdown
            onChange={this.handleQuestionSetorGroupIdChange}
            value={this.state.criteria_id}
          />
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <label>Render Type</label>
          <select
            name="render_type"
            value={this.state.question.render_type}
            onChange={this.handleChange}
            required
          >
            <option value=""> -- Select One -- </option>
            {this.renderModalComponentTypes()
              ? renderProfileSetupTypes.map((upsField) => (
                  <option key={upsField} value={upsField}>
                    {`${upsField}: (UPS Modal)`}
                  </option>
                ))
              : renderProfileFieldTypes.map((profileField) => (
                  <option key={profileField} value={profileField}>
                    {`${profileField}: (EditProfile or OGs Steps)`}
                  </option>
                ))}
          </select>
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <label>Required?</label>
          <Checkbox
            type="checkbox"
            name="required"
            onClick={this.toggleRequired}
            checked={this.state.question.required}
          />
        </StyledColumn>
        <StyledColumn>
          <label>
            Repeatable?{' '}
            {
              <InfoTooltip
                key="is-repeatable-flag"
                tooltip="If checked, this question can be cleared out by a service and then asked again in the UPS"
                uid="is-repeatable-flag"
                iconOnly
              />
            }
          </label>
          <Checkbox
            type="checkbox"
            name="repeatable"
            onClick={this.toggleRepeatable}
            checked={this.state.question.repeatable}
          />
        </StyledColumn>
      </StyledRow>

      {this.state.question.professional_center && (
        <>
          <StyledRow>
            <StyledColumn>
              <label>Filter/Column Name</label>
              <input
                type="text"
                value={this.state.question.professional_center.name}
                name="name"
                onChange={this.handlePCChange}
              />
            </StyledColumn>
          </StyledRow>
          <StyledRow>
            <StyledColumn>
              <label>Display as a Column?</label>
              <Checkbox
                type="checkbox"
                name="column"
                onClick={this.togglePCColumn}
                checked={this.state.question.professional_center.column}
              />
            </StyledColumn>
            <StyledColumn>
              <label>Display as a Filter?</label>
              <Checkbox
                type="checkbox"
                name="filter"
                onClick={this.togglePCFilter}
                checked={this.state.question.professional_center.filter}
              />
            </StyledColumn>
          </StyledRow>
        </>
      )}

      <StyledRow>
        <StyledColumn>
          <label>Expires in: (number)</label>
          <input
            type="number"
            value={this.state.question.expiration_time}
            name="expiration_time"
            onChange={this.handleChange}
          />
        </StyledColumn>
        <StyledColumn>
          <label>Expiration Units</label>
          <select
            value={this.state.question.expiration_units}
            name="expiration_units"
            onChange={this.handleChange}
            disabled={!this.state.question.expiration_time}
          >
            <option value=""></option>
            <option value="seconds">Seconds</option>
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>
        </StyledColumn>
      </StyledRow>
      <FooterRow>
        <SubmitButtons>
          <input
            className="button"
            value="Cancel"
            onClick={this.props.handleCancel}
            readOnly={true}
          />
          <input
            type="submit"
            className="button"
            defaultValue={this.submitButtonText}
            onSubmit={this.handleSubmit}
            disabled={this.state.loading}
            readOnly={true}
          />
        </SubmitButtons>
      </FooterRow>
    </StyledForm>
  );
}

QuestionForm.propTypes = {
  handleCancel: func,
  graph_data: object,
  reload_graph: func,
  loading: bool,
  method: string,
  question: object,
  question_group: object,
  question_set: object,
  answer_choices: array,
  criteria_id: number,
};

QuestionForm.dependantPropPath = 'questionSetId';

QuestionForm.query = async (token, {}) =>
  await Query(`
      query {
        question_sets() {
          results {
            id
            name
          }
        }
      }
  `);

export default withData(QuestionForm);
